import React, { useState, useEffect, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import HistoryIcon from '@material-ui/icons/History';

/**
 * Flyway 관리 탭 — dev·prd flyway_schema_history 비교 뷰 (조회 전용).
 * 설계 SoT: ops/docs/design/15-flyway-tab-design.md (2026-07-15 E1c 승인).
 * P0 = 이력 비교 + 실패 잔존. 파일 열(repo 대조 = history-only/file-only/
 * dev-only 판정)은 P1 — 백엔드 file='unknown' 이면 '—' 표시.
 */

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  tableContainer: {
    maxHeight: 560,
  },
  headerCell: {
    fontWeight: 700,
    backgroundColor: theme.palette.background.default,
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  monoCell: {
    fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontSize: 12,
  },
}));

/* ─── Types (backend flyway.ts 응답과 짝) ─── */

interface HistoryRow {
  installedRank: number;
  version: string | null;
  description: string;
  type: string;
  script: string;
  checksum: string | null;
  installedOn: string;
  executionTime: number;
  success: boolean;
}

interface EnvHistory {
  status: 'ok' | 'no-flyway' | 'not-deployed' | 'error';
  namespace: string;
  rows: HistoryRow[];
  error?: string;
}

type Verdict =
  | 'failed'
  | 'checksum-mismatch'
  | 'history-only'
  | 'prd-only'
  | 'not-promoted'
  | 'dev-only'
  | 'file-only'
  | 'match';

interface MergedRow {
  key: string;
  version: string | null;
  script: string;
  description: string;
  type: string;
  dev: HistoryRow | null;
  prd: HistoryRow | null;
  file: 'present' | 'absent' | 'dev-only' | 'unknown';
  verdict: Verdict;
  failedAgeDays?: number;
}

interface FilesInfo {
  status: 'ok' | 'no-relations' | 'error';
  error?: string;
  services: {
    ref: string;
    slug: string;
    migration: number;
    migrationDev: number;
  }[];
}

interface HistoryResponse {
  dbName: string;
  dbType: string;
  envs: { dev: EnvHistory; prd: EnvHistory };
  files?: FilesInfo;
  merged: MergedRow[];
  summary: Record<Verdict, number>;
}

const FILE_LABEL: Record<MergedRow['file'], string> = {
  'present': '✅',
  'absent': '❌ 없음',
  'dev-only': 'dev 폴더',
  'unknown': '—',
};

/* ─── 판정 표시 ─── */

const VERDICT_LABEL: Record<Verdict, string> = {
  'match': '일치',
  'not-promoted': '미승격',
  'prd-only': 'prd만 (역전)',
  'checksum-mismatch': 'checksum 불일치',
  'history-only': '이력만 잔존',
  'file-only': '파일만 (미적용)',
  'dev-only': 'dev 전용',
  'failed': '실패 잔존',
};

// 색: 정상=default/초록, 정보=파랑, 경고·위험=주황/빨강
const VERDICT_STYLE: Record<Verdict, React.CSSProperties> = {
  'match': { backgroundColor: '#388e3c', color: '#fff' },
  'not-promoted': { backgroundColor: '#1976d2', color: '#fff' },
  'dev-only': { backgroundColor: '#7b1fa2', color: '#fff' },
  'file-only': { backgroundColor: '#616161', color: '#fff' },
  'history-only': { backgroundColor: '#f57c00', color: '#fff' },
  'prd-only': { backgroundColor: '#d32f2f', color: '#fff' },
  'checksum-mismatch': { backgroundColor: '#d32f2f', color: '#fff' },
  'failed': { backgroundColor: '#d32f2f', color: '#fff' },
};

const envCell = (r: HistoryRow | null): string => {
  if (!r) return '—';
  const date = r.installedOn ? r.installedOn.substring(0, 10) : '?';
  return r.success ? `✅ ${date}` : `❌ s=0 ${date}`;
};

/* ─── Main ─── */

export const FlywayContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);

  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dbName = entity?.metadata?.name || '';
  const namespace =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ||
    entity?.metadata?.namespace ||
    '';
  const dbType =
    entity?.metadata?.annotations?.['resource-type'] || 'mysql';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ dbName, namespace, dbType });
      const resp = await fetchApi.fetch(`/api/flyway/history?${params}`);
      const body = await resp.json();
      if (!resp.ok) {
        setError(body.error || `HTTP ${resp.status}`);
      } else {
        setData(body);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dbName, namespace, dbType, fetchApi]);

  useEffect(() => {
    if (dbName && namespace) {
      load();
    }
  }, [dbName, namespace, load]);

  if (!dbName || !namespace) {
    return (
      <Alert severity="warning">
        DB name or namespace annotation not found on this entity.
      </Alert>
    );
  }

  const summaryChips: { verdict: Verdict; alwaysShow?: boolean }[] = [
    { verdict: 'match', alwaysShow: true },
    { verdict: 'not-promoted' },
    { verdict: 'dev-only' },
    { verdict: 'history-only' },
    { verdict: 'checksum-mismatch' },
    { verdict: 'prd-only' },
    { verdict: 'file-only' },
    { verdict: 'failed' },
  ];

  const bothNoFlyway =
    data?.envs.dev.status === 'no-flyway' &&
    data?.envs.prd.status === 'no-flyway';

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <HistoryIcon />
        <Typography variant="h6">Flyway — {dbName}</Typography>
        <Chip size="small" label={dbType.toUpperCase()} variant="outlined" />
        <Chip size="small" label="읽기 전용" variant="outlined" />
        <Box flexGrow={1} />
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        data && (
          <>
          {/* env 상태 이상 안내 — not-deployed = 정보성 (미배포 ≠ 오류) */}
          {(['dev', 'prd'] as const).map(env => {
            const eh = data.envs[env];
            if (eh.status === 'not-deployed') {
              return (
                <Alert key={env} severity="info" style={{ marginBottom: 8 }}>
                  {env} ({eh.namespace}) = 이 DB 미배포 — 비교는 배포된 환경
                  기준으로만 표시됩니다.
                </Alert>
              );
            }
            if (eh.status === 'error') {
              return (
                <Alert key={env} severity="warning" style={{ marginBottom: 8 }}>
                  {env} ({eh.namespace}) 이력 조회 실패: {eh.error}
                </Alert>
              );
            }
            return null;
          })}

          {bothNoFlyway ? (
            <Alert severity="info">
              이 DB 에는 flyway_schema_history 테이블이 없습니다 — Flyway
              미사용 (서비스가 Flyway 마이그레이션을 쓰기 시작하면 자동
              표시됩니다).
            </Alert>
          ) : (
            <>
              {/* 파일 대조 상태 (P1) */}
              {data.files?.status === 'no-relations' && (
                <Alert severity="info" style={{ marginBottom: 8 }}>
                  이 DB 를 쓰는 서비스가 카탈로그 관계(dependsOn)로 등록되어
                  있지 않아 repo 파일 대조(파일 열)를 건너뜁니다 — 서비스
                  catalog-info.yaml 에{' '}
                  <code>
                    dependsOn: [resource:&lt;ns&gt;/{dbName}]
                  </code>{' '}
                  등록 시 자동 활성화됩니다.
                </Alert>
              )}
              {data.files?.status === 'error' && (
                <Alert severity="warning" style={{ marginBottom: 8 }}>
                  repo 파일 대조 실패 (파일 열 = 미상으로 표시):{' '}
                  {data.files.error}
                </Alert>
              )}

              {/* 요약 배지 */}
              <Box className={classes.summaryBar}>
                {data.files?.services.map(s => (
                  <Chip
                    key={s.slug}
                    size="small"
                    label={`서비스 ${s.slug} (mig ${s.migration}·dev ${s.migrationDev})`}
                    variant="outlined"
                  />
                ))}
                <Chip
                  size="small"
                  label={`dev ${data.envs.dev.rows.length}행`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`prd ${data.envs.prd.rows.length}행`}
                  variant="outlined"
                />
                {summaryChips.map(({ verdict, alwaysShow }) => {
                  const count = data.summary?.[verdict] ?? 0;
                  if (!count && !alwaysShow) return null;
                  return (
                    <Chip
                      key={verdict}
                      size="small"
                      label={`${VERDICT_LABEL[verdict]} ${count}`}
                      style={count ? VERDICT_STYLE[verdict] : undefined}
                      variant={count ? 'default' : 'outlined'}
                    />
                  );
                })}
              </Box>

              {/* 병합 표 */}
              <TableContainer className={classes.tableContainer}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell}>Version</TableCell>
                      <TableCell className={classes.headerCell}>Script</TableCell>
                      <TableCell className={classes.headerCell}>Description</TableCell>
                      <TableCell className={classes.headerCell}>파일</TableCell>
                      <TableCell className={classes.headerCell}>dev</TableCell>
                      <TableCell className={classes.headerCell}>prd</TableCell>
                      <TableCell className={classes.headerCell}>판정</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.merged.map(m => (
                      <TableRow key={m.key} hover>
                        <TableCell className={classes.monoCell}>
                          {m.version ?? 'R'}
                        </TableCell>
                        <TableCell className={classes.monoCell}>
                          <Tooltip
                            title={
                              <>
                                <div>type: {m.type}</div>
                                <div>
                                  checksum dev: {m.dev?.checksum ?? '—'} / prd:{' '}
                                  {m.prd?.checksum ?? '—'}
                                </div>
                                <div>
                                  rank dev: {m.dev?.installedRank ?? '—'} / prd:{' '}
                                  {m.prd?.installedRank ?? '—'}
                                </div>
                                <div>
                                  exec(ms) dev: {m.dev?.executionTime ?? '—'} /
                                  prd: {m.prd?.executionTime ?? '—'}
                                </div>
                              </>
                            }
                          >
                            <span>{m.script}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{m.description}</TableCell>
                        <TableCell className={classes.monoCell}>
                          {FILE_LABEL[m.file] ?? '—'}
                        </TableCell>
                        <TableCell className={classes.monoCell}>
                          {envCell(m.dev)}
                        </TableCell>
                        <TableCell className={classes.monoCell}>
                          {envCell(m.prd)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              m.verdict === 'failed' &&
                              m.failedAgeDays !== undefined
                                ? `${VERDICT_LABEL.failed} ${m.failedAgeDays}d`
                                : VERDICT_LABEL[m.verdict]
                            }
                            style={VERDICT_STYLE[m.verdict]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          </>
        )
      )}
    </Box>
  );
};
